import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { api, clearAuth, getUser, getToken, BACKEND_URL } from "@/api";
import { Bell, LogOut, ShieldCheck, Home, MessageSquare, Calculator, ScanLine, User, Crown, LifeBuoy, Menu, X, Bug, Mail, Handshake } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import AuthPrompt from "@/components/AuthPrompt";

const NavLink = ({ to, icon: Icon, label, current }) => (
  <Link to={to} data-testid={`nav-${label.toLowerCase()}`}
    className={`flex items-center gap-2 px-4 py-2 rounded-2xl font-medium btn-pop ${current ? "bg-[#ff6fa3] text-white shadow-cartoon-sm" : "hover:bg-[#ffd1e8]/60 text-[#2a1f3d]"}`}>
    <Icon className="w-4 h-4" /> <span className="hidden md:inline">{label}</span>
  </Link>
);
const MobileNavLink = ({ to, icon: Icon, label, current, onClick }) => (
  <Link to={to} onClick={onClick}
    className={`flex items-center gap-3 px-4 py-3 rounded-2xl font-medium text-base ${current ? "bg-[#ff6fa3]/20 text-[#ff5a8a]" : "hover:bg-[#ffd1e8]/40 text-[#2a1f3d]"}`}>
    <Icon className="w-5 h-5" /> {label}
  </Link>
);

function BugReportDialog({ open, onOpenChange }) {
  const [subject, setSubject] = useState("");
  const [desc, setDesc] = useState("");
  const [screenshot, setScreenshot] = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState(null);
  const [sending, setSending] = useState(false);
  const [cooldown, setCooldown] = useState(null);

  useEffect(() => {
    if (!open) return;
    const last = localStorage.getItem("bug_report_last");
    if (last) {
      const elapsed = Date.now() - Number(last);
      const remaining = 2 * 60 * 60 * 1000 - elapsed;
      if (remaining > 0) {
        setCooldown(Math.ceil(remaining / 60000));
        return;
      }
    }
    setCooldown(null);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      for (const item of items) {
        if (item.type.startsWith("image/")) {
          e.preventDefault();
          const file = item.getAsFile();
          if (!file) continue;
          if (file.size > 5 * 1024 * 1024) { toast.error("Image must be under 5MB."); return; }
          setScreenshot(file);
          const reader = new FileReader();
          reader.onload = () => setScreenshotPreview(reader.result);
          reader.readAsDataURL(file);
          toast.success("Image pasted!");
          return;
        }
      }
    };
    document.addEventListener("paste", handler);
    return () => document.removeEventListener("paste", handler);
  }, [open]);

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Only image files are allowed."); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Image must be under 5MB."); return; }
    setScreenshot(file);
    const reader = new FileReader();
    reader.onload = () => setScreenshotPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const submit = async () => {
    if (!subject.trim() || sending || cooldown) return;
    setSending(true);
    try {
      const body = { subject, description: desc, page_url: window.location.href };
      if (screenshot) body.screenshot = screenshotPreview;
      await api.post("/bug-reports", body);
      toast.success("Bug report submitted! Thank you.");
      localStorage.setItem("bug_report_last", String(Date.now()));
      setSubject(""); setDesc(""); setScreenshot(null); setScreenshotPreview(null); onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send report");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl bg-white border-4 border-yellow-300 max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[#2a1f3d] flex items-center gap-2">
            <Bug className="w-5 h-5 text-yellow-600"/> Report a Bug
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {cooldown && (
            <div className="rounded-2xl bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800 text-center">
              You can submit another bug report in <b>{cooldown}</b> minute{cooldown > 1 ? "s" : ""}.
            </div>
          )}
          <Input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Short summary of the issue..." className="rounded-2xl" disabled={!!cooldown}/>
          <Textarea value={desc} onChange={e => setDesc(e.target.value)} placeholder="Describe what happened, steps to reproduce, and what you expected..." className="rounded-2xl" rows={4} disabled={!!cooldown}/>
          <div className="flex flex-wrap items-center gap-3">
            <label className="cursor-pointer btn-pop">
              <input type="file" accept="image/*" onChange={handleFile} className="hidden" disabled={!!cooldown}/>
              <span className="inline-flex items-center gap-1.5 rounded-2xl border-2 border-dashed border-[#2a1f3d]/20 px-4 py-2 text-sm text-[#2a1f3d]/60 hover:border-[#7649e6]/40 hover:text-[#7649e6] transition-colors">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>
                {screenshot ? "Change image" : "Add image"}
              </span>
            </label>
            <span className="text-xs text-[#2a1f3d]/40">or paste (Ctrl+V)</span>
            {screenshotPreview && (
              <div className="relative">
                <img src={screenshotPreview} alt="Preview" className="h-14 w-14 rounded-xl object-cover border border-[#2a1f3d]/10"/>
                <button onClick={() => { setScreenshot(null); setScreenshotPreview(null); }} className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-[10px] leading-none">&times;</button>
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-2xl">Cancel</Button>
          <Button onClick={submit} disabled={!subject.trim() || sending || !!cooldown} className="rounded-2xl bg-yellow-500 hover:bg-yellow-600 text-white">{sending ? "Sending..." : "Send Report"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Shell({ children }) {
  const user = getUser();
  const loc = useLocation();
  const nav = useNavigate();
  const [notifs, setNotifs] = useState([]);
  const [open, setOpen] = useState(false);
  const [mobOpen, setMobOpen] = useState(false);
  const [bugOpen, setBugOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    api.get("/notifications").then((r) => setNotifs(r.data.notifications)).catch(() => {});
    if ("Notification" in window && Notification.permission === "default") { Notification.requestPermission(); }
    const t = getToken();
    if (!t) return;
    const wsUrl = BACKEND_URL.replace(/^https?/, BACKEND_URL.startsWith("https") ? "wss" : "ws") + `/api/ws?token=${t}`;
    let ws;
    try {
      ws = new WebSocket(wsUrl);
      ws.onmessage = (ev) => {
        try {
          const d = JSON.parse(ev.data);
          if (d.type === "notification") {
            setNotifs((p) => [d.notification, ...p]);
            toast(d.notification.content, { description: d.notification.type });
            if (document.hidden && "Notification" in window && Notification.permission === "granted") {
              const n = d.notification;
              new Notification("AdoptAI", { body: n.content, icon: "/pets/shadow.png" });
            }
          } else if (d.type === "message") {
            window.dispatchEvent(new CustomEvent("ws:message", { detail: d }));
          } else if (d.type === "trade_update") {
            window.dispatchEvent(new CustomEvent("ws:trade_update", { detail: d }));
          }
        } catch (e) { console.error("ws message parse failed", e); }
      };
    } catch (e) { console.error("ws connect failed", e); }
    return () => { try { ws && ws.close(); } catch (e) { console.error("ws close failed", e); } };
  }, []);

  const unread = notifs.filter((n) => !n.read).length;
  const logout = () => { clearAuth(); nav("/"); };
  const isAdmin = user && ["owner","admin","moderator"].includes(user.role);

  return (
    <div className="min-h-screen bg-cartoon bg-grain relative">
      <div className="floaty absolute top-12 left-8 w-24 h-24 rounded-full bg-[#ffb6d5]/50 blur-2xl pointer-events-none" />
      <div className="floaty-2 absolute top-32 right-16 w-32 h-32 rounded-full bg-[#a3e1ff]/50 blur-2xl pointer-events-none" />
      <div className="floaty-3 absolute bottom-20 left-1/3 w-40 h-40 rounded-full bg-[#fff1a8]/60 blur-3xl pointer-events-none" />

      <header className="sticky top-0 z-40 backdrop-blur-md bg-white/70 border-b-2 border-[#2a1f3d]/10">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 flex items-center gap-2">
          <Link to="/" className="flex items-center gap-2 font-display text-xl sm:text-2xl text-[#ff5a8a] shrink-0">
            <div className="relative w-10 h-10 sm:w-12 sm:h-12">
              <img src="/pets/shadow.png" alt="" className="logo-pet" style={{ left: -20, top: -6, animation: "logo-float 3s ease-in-out infinite" }}/>
              <img src="/pets/frost.png" alt="" className="logo-pet" style={{ right: -20, top: -8, animation: "logo-float-reverse 3.4s ease-in-out infinite" }}/>
              <img src="/pets/parrot.png" alt="" className="logo-pet" style={{ left: 6, bottom: -14, animation: "logo-bounce 2.8s ease-in-out infinite" }}/>
            </div>
            <span className="hidden xs:inline">Adopt<span className="text-[#7649e6]">AI</span></span>
          </Link>
          {/* Mobile hamburger */}
          <button onClick={() => setMobOpen(o => !o)} className="md:hidden p-2 rounded-xl hover:bg-[#ffd1e8]/60 btn-pop" aria-label="Menu">
            {mobOpen ? <X className="w-5 h-5 text-[#2a1f3d]"/> : <Menu className="w-5 h-5 text-[#2a1f3d]"/>}
          </button>
          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-1 ml-2">
            <NavLink to="/" icon={Home} label="Home" current={loc.pathname === "/"} />
            <NavLink to="/calculator" icon={Calculator} label="Calculator" current={loc.pathname.startsWith("/calculator")} />
            <NavLink to="/scanner" icon={ScanLine} label="Scanner" current={loc.pathname.startsWith("/scanner")} />
            <NavLink to="/trades" icon={MessageSquare} label="Trades" current={loc.pathname.startsWith("/trades")} />
            <NavLink to="/messages" icon={Mail} label="Messages" current={loc.pathname.startsWith("/messages")} />
            <NavLink to="/support" icon={LifeBuoy} label="Support" current={loc.pathname.startsWith("/support")} />
            <NavLink to="/partnerships" icon={Handshake} label="Partners" current={loc.pathname.startsWith("/partnerships")} />
          </nav>
          <div className="ml-auto flex items-center gap-1 sm:gap-2">
            {user ? (<>
              <button data-testid="bell-button" onClick={() => setOpen((o) => !o)} className="relative p-2 rounded-full bg-white shadow-cartoon-sm btn-pop">
                <Bell className="w-5 h-5 text-[#7649e6]" />
                {unread > 0 && <span className="absolute -top-1 -right-1 bg-[#ff5a8a] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">{unread}</span>}
              </button>
              {open && (
                <div className="absolute right-4 top-16 w-80 max-h-96 overflow-auto card-cartoon p-3 z-50">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-display text-lg">Notifications</span>
                    <button data-testid="mark-all-read" onClick={async () => { await api.post("/notifications/read-all"); setNotifs(notifs.map(n => ({...n, read: true}))); }} className="text-xs text-[#7649e6] underline">Mark all read</button>
                  </div>
                  {notifs.length === 0 && <div className="text-sm text-gray-500 py-6 text-center">No notifications yet</div>}
                  {notifs.map(n => {
                    const meta = n.meta || {};
                    return (
                    <div key={n.id} onClick={() => { const path = meta.trade_id ? `/messages?trade=${meta.trade_id}${meta.with_user_id ? `&with=${meta.with_user_id}` : ""}` : null; if (path) { nav(path); setOpen(false); } }} className={`p-2 rounded-xl mb-1 text-sm ${n.read ? "bg-gray-50" : "bg-[#fff1a8]/40 border-2 border-[#ffb13b]/30"} ${meta.trade_id ? "cursor-pointer hover:bg-[#ffd1e8]/40" : ""}`}>
                      <div className="font-medium">{n.type === "new_offer" ? "New Offer" : n.type === "trade_completed" ? "Trade Completed" : n.type === "trade_complete_request" ? "Completion Request" : n.type === "trade_confirm" ? "Confirmation" : n.type === "new_review" ? "New Review" : n.type === "message" ? "New Message" : n.type}</div>
                      <div className="text-xs">{n.content}</div>
                    </div>
                  );})}
                </div>
              )}
              <Link to={`/profile/${user.roblox_username_lc || user.roblox_username}`} data-testid="profile-link" className="flex items-center gap-2 px-3 py-2 rounded-2xl bg-white shadow-cartoon-sm btn-pop">
                {user.avatar_url && <img src={user.avatar_url} alt="avatar" className="w-7 h-7 rounded-full" />}
                <span className="font-medium hidden sm:inline">{user.roblox_username}</span>
                {user.role !== "user" && <Badge className="bg-gradient-to-r from-[#ffb13b] to-[#ff5a8a] text-white capitalize">{user.role}</Badge>}
              </Link>
              <Button data-testid="logout-button" variant="ghost" onClick={logout} className="rounded-2xl"><LogOut className="w-4 h-4" /></Button>
              {isAdmin && <NavLink to="/admin" icon={Crown} label="Admin" current={loc.pathname.startsWith("/admin")} />}
            </>) : (
              <Link to="/login"><Button data-testid="login-button" className="rounded-2xl bg-[#ff6fa3] hover:bg-[#ff5a8a] shadow-cartoon-sm btn-pop">Verify with Roblox</Button></Link>
            )}
          </div>
        </div>
      </header>
      {/* Mobile nav dropdown */}
      {mobOpen && <div className="md:hidden bg-white/95 backdrop-blur-md border-b-2 border-[#2a1f3d]/10 shadow-cartoon-sm px-3 py-3 flex flex-col gap-1 fixed top-[57px] inset-x-0 z-30 animate-in slide-in-from-top">
        <MobileNavLink to="/" icon={Home} label="Home" current={loc.pathname === "/"} onClick={() => setMobOpen(false)} />
        <MobileNavLink to="/calculator" icon={Calculator} label="Calculator" current={loc.pathname.startsWith("/calculator")} onClick={() => setMobOpen(false)} />
        <MobileNavLink to="/scanner" icon={ScanLine} label="Scanner" current={loc.pathname.startsWith("/scanner")} onClick={() => setMobOpen(false)} />
        <MobileNavLink to="/trades" icon={MessageSquare} label="Trades" current={loc.pathname.startsWith("/trades")} onClick={() => setMobOpen(false)} />
        <MobileNavLink to="/messages" icon={Mail} label="Messages" current={loc.pathname.startsWith("/messages")} onClick={() => setMobOpen(false)} />
        <MobileNavLink to="/support" icon={LifeBuoy} label="Support" current={loc.pathname.startsWith("/support")} onClick={() => setMobOpen(false)} />
        <MobileNavLink to="/partnerships" icon={Handshake} label="Partners" current={loc.pathname.startsWith("/partnerships")} onClick={() => setMobOpen(false)} />
        {isAdmin && <MobileNavLink to="/admin" icon={Crown} label="Admin" current={loc.pathname.startsWith("/admin")} onClick={() => setMobOpen(false)} className="mt-2 border-t-2 border-[#2a1f3d]/10 pt-2"/>}
      </div>}
      <main className="relative z-10 max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-6">{children}</main>
      <footer className="relative z-10 text-center text-sm text-[#7649e6]/70 py-8">
        <div className="flex items-center justify-center gap-4 flex-wrap">
          <span>AdptMeValues • Adopt Me values, smarter together</span>
          <button onClick={() => setBugOpen(true)} className="inline-flex items-center gap-1 text-xs text-yellow-600 hover:text-yellow-700 underline underline-offset-2">
            <Bug className="w-3 h-3"/> Report a Bug
          </button>
        </div>
      </footer>
      <BugReportDialog open={bugOpen} onOpenChange={setBugOpen}/>
    </div>
  );
}

export const Protected = ({ children }) => {
  const [open, setOpen] = useState(false);
  const user = getUser();
  useEffect(() => { if (!user) setOpen(true); }, [user]);
  if (!user) return <>
    <AuthPrompt open={open} onOpenChange={setOpen} title="Sign in to continue" description="You need to verify with Roblox to access this page." />
    <div className="flex items-center justify-center min-h-[50vh]">
      <div className="card-cartoon p-8 text-center max-w-sm">
        <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-rose-100">
          <ShieldCheck className="h-6 w-6 text-rose-500" />
        </div>
        <div className="font-display text-xl mb-1">Sign in required</div>
        <p className="text-sm text-muted-foreground mb-4">Verify with Roblox to access this page.</p>
        <Link to="/login" className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-[#ff5a8a] to-[#b13bff] text-white font-bold px-6 py-2.5 btn-pop text-sm">Verify with Roblox</Link>
      </div>
    </div>
  </>;
  return children;
};
